import { Component, EventEmitter, Output, ViewChild, AfterViewInit, DestroyRef, Input, NgZone } from '@angular/core';
import { Message, MessageSendConfig, NewMessage, PollWithOptions, PollWithOptionsAndResponse, SendMessageReceipients } from 'src/app/models/message';
import { CommService } from '../comm.service';
import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { ImageUtilsService } from '../../shared/image-utils.service';
import { NotificationService } from 'src/app/shared/notification.service';


@Component({
  selector: 'app-new-comm',
  templateUrl: './new-comm.component.html',
  styleUrls: ['./new-comm.component.less']
})
export class NewCommComponent {
  @ViewChild('form') form!: NgForm;
  @ViewChild('quillEditor', { static: false }) quillEditor: any;
  @ViewChild('sendCommComponent') sendCommComponent: any;
  @ViewChild('pollingComponent') pollingComponent: any;

  @Input() availableRecipients: SendMessageReceipients = { clients: [], franchises: [], roles: [] };
  @Output() sendMessage = new EventEmitter<NewMessage>();
  @Output() previewMessage = new EventEmitter<NewMessage>();

  isLoading = false;
  maxImageWidth = 500;
  maxImageHeight = 300;
  maxFileSize = 2;
  imageQuality = 1;

  maxFileSizePdfs = 5
  uploadedPdf: File | null = null;
  pdfPreview: { name: string, size: string, url: string } | null = null;

  quillConfig = {
    toolbar: [
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': ['', 'center', 'right', 'justify'] }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['image'],
      ['video'],
      ['clean']
    ]
  };

  messageForm = new FormGroup({
    title: new FormControl('', [Validators.required]),
    content: new FormControl('', [Validators.required]),
    isPopup: new FormControl(false),
    pdf: new FormControl(null),
  });

  private quillInstance: any;

  private currentSendConfig: MessageSendConfig = {
    franchiseIds: [],
    clientIds: [],
    roleIds: []
  };

  private currentPoll: PollWithOptions | undefined;

  get messagePreview(): NewMessage {
    const formValue = this.messageForm.value;
    return {
      messageId: 0,
      userId: 0,
      dateCreated: new Date(),
      dateRead: null,
      message: formValue.title || '',
      messageLong: formValue.content || '',
      value: 0,
      messageType: null,
      messageOpened: false,
      messageGroupId: '',
      pdfBase64: this.pdfPreview?.url || null,
      isPopup: formValue.isPopup || false,
      sendConfig: this.currentSendConfig,
      createdByUserId: 0,
      action1Url: null,
      action1Text: null,
      poll: this.currentPoll
    };
  }

  constructor(
    public commService: CommService,
    private imageUtilsService: ImageUtilsService,
    private notification : NotificationService,
    private ngZone: NgZone
  ) { 
    this.messageForm.valueChanges.subscribe(() => {
      this.previewMessage.emit(this.messagePreview);
    });
  }

  public onEditorCreated(quill: any): void {
      this.quillInstance = quill;
      const toolbar = quill.getModule('toolbar');
      toolbar.addHandler('image', () => this.handleImageUpload());
    }

  public async handlePdfUpload(event: Event): Promise<void> {
      this.isLoading = true;
      try {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
          if (file.type !== 'application/pdf') {
            this.notification.showError('Please select a valid PDF file.');
            this.isLoading = false;
            return;
          }
          if (file.size > this.maxFileSizePdfs * 1024 * 1024) {
            this.notification.showError(`PDF size exceeds ${this.maxFileSizePdfs} MB limit.`);
            this.isLoading = false;
            return;
          }
          this.uploadedPdf = file;
          const base64Url = await this.fileToBase64(file);
          
          this.pdfPreview = {
            name: file.name,
            size: (file.size / 1024).toFixed(2) + ' KB',
            url: base64Url
          };

        }
      } catch (error) {
        this.notification.showError('Failed to upload PDF.');
      } finally {
        this.isLoading = false;
      }
    }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }
    
  public removePdf(): void {
    this.uploadedPdf = null;
    this.pdfPreview = null;
  }

  public onSubmit(): void {
    if (this.messagePreview.poll?.pollId) {
    const isPollValid = this.pollingComponent.validateAllOptions();
    if (!isPollValid) {
      this.notification.showError('Please fill in all poll option texts.');
      return;
    }
  }
    if (this.messageForm.valid && this.isConfigValid()) {
      this.sendMessage.emit(this.messagePreview);
      this.resetForm();
    }
  }

  private isConfigValid(): boolean {
    return !!(
      this.currentSendConfig.franchiseIds?.length &&
      this.currentSendConfig.clientIds?.length &&
      this.currentSendConfig.roleIds?.length
    );
  }

  public receivedSendMessageConfig(config: MessageSendConfig) {
    this.currentSendConfig = config;
    this.previewMessage.emit(this.messagePreview);
  }

  public receivedNewPoll(poll: PollWithOptions){
    this.currentPoll = poll;
    this.previewMessage.emit(this.messagePreview);
  }

  public handleImageUpload(): void {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        try {
          this.isLoading = true;

          if (!this.imageUtilsService.validateFileSize(file, this.maxFileSize)) {
            return;
          }

          const resizeResult = await this.imageUtilsService.resizeImage(file, {
            maxWidth: this.maxImageWidth,
            maxHeight: this.maxImageHeight,
            quality: this.imageQuality
          });
          this.ngZone.run(() => {
            this.insertImageIntoQuill(resizeResult.base64);
            this.isLoading = false;
          });
        } catch (error) {
          alert('Error processing image. Please try again.');
        } finally {
          this.isLoading = false;
        }
      }
    };
    
  input.click();

  }

  private insertImageIntoQuill(imageBase64: string): void {
    if (this.quillInstance) {
      const range = this.quillInstance.getSelection(true) || { index: 0 };
      
      this.quillInstance.insertEmbed(range.index, 'image', imageBase64);
      
      this.quillInstance.setSelection(range.index + 1);

      const content = this.quillInstance.root.innerHTML;
      this.messageForm.controls['content'].setValue(content, { emitEvent: true });
      
    } else {

    }
  }

  public resetForm(): void {
    this.messageForm.reset();
    this.currentSendConfig = { franchiseIds: [], clientIds: [], roleIds: [] };
    this.currentPoll = undefined;
    this.sendCommComponent.clearAllSelections();
    this.pollingComponent.resetPoll();
  }
}